import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import authService from 'services/authService';
import useSnackbar from 'hooks/useSnackbar';

// material core
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tabs,
  Tab,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  MenuItem,
  Paper,
  IconButton,
  Grid,
  Select
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import Close from '@material-ui/icons/Close';

// actions
import { setLoading, setReAssignDialog } from 'actions/app.action';

// selectors
import { reAssignDialogSelector } from 'selectors/app.selector';
import { roleSelector } from 'selectors/auth.selector';

import { API_URLS } from 'utils/apiUtils';
import axios from 'axios';

interface TabPanelProps {
  children?: React.ReactNode;
  index: any;
  value: any;
}
function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && (
        <div style={{ marginTop: 16 }}>
          {children}
        </div>
      )}
    </div>
  );
}

function a11yProps(index: any) {
  return {
    id: `tab-${index}`,
    'aria-controls': `tabpanel-${index}`,
  };
}

const useStyles = makeStyles({
  root: {
    paddingLeft: 20,
    paddingRight: 20,
    // paddingTop: 10,
    paddingBottom: 10,
    borderRadius: 7,
  },

  btnContainer: {
    paddingTop: 20,
    paddingBottom: 20,
  },

  subHeading: {
    paddingTop: 20,
  },

  imagePaperTitle: {
    padding: 15,
    borderRadius: 0,
  },

  imgContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 1,
  },
  actionContainer: {
    display: 'flex',
    paddingLeft: 20,
    paddingRight: 20,
    justifyContent: 'space-between',
  },
  textArea: {
    width: '100%',
    borderColor: '#c4c4c4',
    outline: 'none',
    fontSize: 16,
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    padding: 10,
  },

  flexRow: {
    display: 'flex',
    flexDirection: 'row',
    gap: 5,
    alignItems: 'center',
  },
  fileInputContainer: {
    border: '1px solid #b4b4b4',
    borderRadius: 3,
    padding: 10,
  },
});

export default function ReAssignDialog() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { isShow, complaint } = useSelector(reAssignDialogSelector);
  const token = authService.getAccessToken();
  const { showSnackBar } = useSnackbar();

  const [value, setValue] = useState(0);

  const [reassignmentRequests, setReassignmentRequests] = useState<any[]>([]);

  const [selectedDepartment, setSelectedDepartment] = useState("");
  const [reason, setReason] = useState("");

  const role = useSelector(roleSelector);
  
  const handleClose = () => {
    dispatch(setReAssignDialog(false, null));
  };

  const handleChange = (event: React.ChangeEvent<{}>, newValue: number) => {
    setValue(newValue);
  };

  useEffect(() => {
    if (complaint !== null) {
      fetchReassignmentRequests();
    }
  }, [complaint]);

  const fetchReassignmentRequests = async () => {
    dispatch(setLoading(true));

    try {
      const res = await axios.get(
        `${API_URLS.ADMIN_GET_COMPLAINT_REASSIGNMENT_REQUESTS}/${complaint._id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setReassignmentRequests(res.data || []);
    } catch (err: any) {
      console.log(err.message);
      showSnackBar(err.response?.data?.message || "Failed to fetch", "error");
    }

    dispatch(setLoading(false));
  };

  const submitReassign = async () => {
    if (!selectedDepartment) {
      return showSnackBar("Please select a department", "error");
    }
    if (!reason.trim()) {
      return showSnackBar("Please enter a reason", "error");
    }

    dispatch(setLoading(true));

    const payload = {
      complaintId: complaint._id,
      requestedBy: complaint.assignedTo, 
      requestedTo: selectedDepartment,
      reason
    };

    try {
      await axios.post(API_URLS.ADMIN_CREATE_COMPLAINT_REASSIGNMENT, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      showSnackBar("Reassignment request submitted");

      // Reset & refresh
      setSelectedDepartment("");
      setReason("");
      fetchReassignmentRequests();

      setValue(0);
    } catch (err: any) {
      showSnackBar(err.response?.data?.message || "Submission failed", "error");
    }

    dispatch(setLoading(false));
  };

  return (
    <Dialog open={isShow} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle disableTypography style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5">Reassignment Request</Typography>

        <IconButton onClick={handleClose}>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Tabs value={value} onChange={handleChange}>
          <Tab label="Reassignment History" {...a11yProps(0)} />
          <Tab label="Request Reassignment" {...a11yProps(1)} />
        </Tabs>

        <TabPanel value={value} index={0}>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Request Date</TableCell>
                  <TableCell>Department</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Reason</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reassignmentRequests.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      No reassignment requests found
                    </TableCell>
                  </TableRow>
                ) : (
                  reassignmentRequests.map((row, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{new Date(row.requestedAt).toLocaleString()}</TableCell>
                      <TableCell>{row.requestedTo}</TableCell>
                      <TableCell>{row.status}</TableCell>
                      <TableCell>{row.reason}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        <TabPanel value={value} index={1}>
          {role === 'ADMIN' && (
            <Grid item xs={12} md={4}>
              <h4>Assign To</h4>
              <div className={classes.flexRow}>
                <Select
                  fullWidth
                  aria-label="Action"
                  variant="outlined"
                  placeholder="Assign"
                  value={selectedDepartment}
                  onChange={async (event: any) => {
                    setSelectedDepartment(event.target.value);
                  }}
                  disabled={complaint?.complaintStatus !== 'Active' || complaint?.complaintStatus !== 'ActionInitiated'}
                >
                  <MenuItem value={'PMU'}>PMU</MenuItem>
                  <MenuItem value={'DEPWD'}>DePWD</MenuItem>
                  <MenuItem value={'CCPD'}>CCPD</MenuItem>
                  <MenuItem value={'SCPD'}>SCPD</MenuItem>
                </Select>
              </div>
            </Grid>
          )}

          {role !== 'ADMIN' && (
            <Grid item xs={12} md={4}>
              <h4>Assign To</h4>
              <div className={classes.flexRow}>
                <Select
                  fullWidth
                  aria-label="Action"
                  variant="outlined"
                  placeholder="Assign"
                  value={selectedDepartment}
                  onChange={async (event: any) => {
                    setSelectedDepartment(event.target.value);
                  }}
                  disabled={complaint?.complaintStatus !== 'Active' || complaint?.complaintStatus !== 'ActionInitiated'}
                >
                  <MenuItem value={'DEPWD'}>DePWD</MenuItem>
                  <MenuItem value={'CCPD'}>CCPD</MenuItem>
                  <MenuItem value={'SCPD'}>SCPD</MenuItem>
                </Select>
              </div>
            </Grid>
          )}

          <TextField
            fullWidth
            label="Reason"
            multiline
            minRows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            margin="normal"
          />

          <Button
            variant="contained"
            color="primary"
            onClick={submitReassign}
            style={{ marginTop: 16 }}
          >
            Submit Request
          </Button>
        </TabPanel>
      </DialogContent>
    </Dialog>
  );
}
